import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Ban, CheckCircle2, Loader2, MessageCircle, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import * as supportConversationsApi from '../api/endpoints/supportConversations';
import { useToast } from '../context/ToastContext';

// This page is a thin display/orchestration layer over existing backend
// decisions — it never determines cancellation eligibility or outcome
// itself. Eligibility comes from order.availableActions.canCancel (computed
// server-side by isSaleCancellableByCustomer, see
// freshnhomely-backend/src/services/orderCancellationService.js), and the
// cancellation itself is requested via the existing customer-app
// support-conversations endpoint (intent: 'cancel'), the only real
// customer-triggerable path into applyApprovedCancellation() today.

// Conversation statuses this page polls for. The support conversation sits
// in one of these once the customer submits it; staff review moves it to
// 'approved' (cancelled) or 'rejected' (kept). This page just reflects
// whatever the backend reports here, never reimplements the transition.
const CONVERSATION_STATUS = {
  open: 'open',
  processing: 'processing',
  approved: 'approved',
  rejected: 'rejected',
  resolved: 'resolved',
};

const POLL_MS = 4000;
const MAX_POLLS = 30; // ~2 minutes

const formatCurrency = (value) => (value != null ? `₹${value}` : '₹0');

const BotBubble = ({ children }) => (
  <div className="flex justify-start">
    <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-emerald-100 shadow-soft px-4 py-3 text-sm leading-relaxed text-emerald-950">
      {children}
    </div>
  </div>
);

const CancelOrderChatPage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [order, setOrder] = useState(state?.order || null);
  const [orderStatus, setOrderStatus] = useState(state?.order ? 'success' : 'loading');
  const [orderError, setOrderError] = useState(null);

  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState('review'); // review | confirm | submitting | polling | done-approved | done-rejected | done-resolved | error
  const [conversation, setConversation] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [selectingMethod, setSelectingMethod] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);
  const [recoveryAttempt, setRecoveryAttempt] = useState(0);

  const pollTimeoutRef = useRef(null);
  const pollCountRef = useRef(0);
  const lastConversationIdRef = useRef(null);

  usePageMetadata({
    title: 'Cancel Order | Fresh & Homely',
    description: 'Request cancellation for your Fresh & Homely order.',
    canonicalPath: `/orders/${id}/cancel`,
  });

  // Always refetch order details fresh from the backend rather than trusting
  // only the state handed off from OrderDetailPage — availableActions.canCancel
  // must reflect the current server state, not a stale snapshot from
  // whenever the customer clicked the button.
  useEffect(() => {
    let cancelled = false;
    setOrderStatus('loading');
    ordersApi
      .getOrder(id)
      .then((response) => {
        if (cancelled) return;
        setOrder(response.data);
        setOrderStatus('success');
      })
      .catch((err) => {
        if (cancelled) return;
        setOrderError(err.message);
        setOrderStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => () => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  }, []);

  const canCancel = Boolean(order?.availableActions?.canCancel);
  const pendingRequestId = order?.availableActions?.pendingCancellationRequestId || null;
  const cancellationInfo = order?.cancellation || null;
  const needsConversationRecovery = Boolean(
    pendingRequestId || cancellationInfo?.refundStatus === 'pending'
  );

  // 'resolved' alone doesn't prove the sale was cancelled — re-fetch the
  // order and trust its own `cancellation` field (populated straight from
  // the Sale document) to decide what actually happened.
  const refreshOrderAndFinish = useCallback(() => {
    ordersApi
      .getOrder(id)
      .then((response) => {
        setOrder(response.data);
        setPhase(response.data?.cancellation ? 'done-approved' : 'done-resolved');
        if (response.data?.cancellation) showToast('Your order has been cancelled');
      })
      .catch((err) => {
        setSubmitError(err.message);
        setPhase('error');
      });
  }, [id, showToast]);

  const pollConversation = useCallback((conversationId) => {
    lastConversationIdRef.current = conversationId;
    supportConversationsApi
      .getSupportConversation(conversationId)
      .then((response) => {
        const data = response.data;
        setConversation(data);

        // 'resolved' is a generic staff close-out used for every conversation
        // intent (cancel/return/exchange/etc.), not just an approved
        // cancellation — staff can resolve an open ticket (e.g. "handled by
        // phone", duplicate ticket) without ever approving the cancellation.
        // Only order.cancellation (populated from the Sale itself) is proof
        // the order was actually cancelled, so re-check that instead of
        // trusting the conversation status alone.
        if (data.status === CONVERSATION_STATUS.approved) {
          setPhase('done-approved');
          showToast('Your order has been cancelled');
          return;
        }
        if (data.status === CONVERSATION_STATUS.rejected) {
          setPhase('done-rejected');
          showToast('Cancellation request was not approved', { tone: 'error' });
          return;
        }
        if (data.status === CONVERSATION_STATUS.resolved) {
          refreshOrderAndFinish();
          return;
        }

        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          setPhase('polling-timeout');
          return;
        }
        pollTimeoutRef.current = setTimeout(() => pollConversation(conversationId), POLL_MS);
      })
      .catch((err) => {
        setSubmitError(err.message);
        setPhase('error');
      });
  }, [refreshOrderAndFinish, showToast]);

  // A cancellation can be approved while the customer is away from this page.
  // Recover the linked conversation so a pending paid refund never loses its
  // destination controls after refresh, navigation, or a polling timeout.
  useEffect(() => {
    if (orderStatus !== 'success' || !order?._id || !needsConversationRecovery) return undefined;

    let active = true;
    setRecoveryError(null);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

    supportConversationsApi
      .listSupportConversations({ saleId: order._id, limit: 10 })
      .then((response) => {
        if (!active) return;
        const recovered = (response.data || []).find((item) => (
          item.intent === 'cancel' || item.refund?.source === 'cancellation'
        ));
        if (!recovered) {
          throw new Error('The cancellation conversation could not be found.');
        }

        setConversation(recovered);
        lastConversationIdRef.current = recovered._id;

        if (cancellationInfo) {
          setPhase('done-approved');
          return;
        }
        if (recovered.status === CONVERSATION_STATUS.rejected) {
          setPhase('done-rejected');
          return;
        }
        if (
          recovered.status === CONVERSATION_STATUS.approved
          || recovered.status === CONVERSATION_STATUS.resolved
        ) {
          refreshOrderAndFinish();
          return;
        }

        setPhase('polling');
        pollCountRef.current = 0;
        pollConversation(recovered._id);
      })
      .catch((err) => {
        if (active) setRecoveryError(err.message);
      });

    return () => {
      active = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [
    cancellationInfo,
    needsConversationRecovery,
    order?._id,
    orderStatus,
    pollConversation,
    recoveryAttempt,
    refreshOrderAndFinish,
  ]);

  const handleConfirmCancel = async () => {
    setPhase('submitting');
    setSubmitError(null);
    try {
      const response = await supportConversationsApi.createSupportConversation({
        intent: 'cancel',
        saleId: id,
        intake: { reason: reason.trim() || 'Customer requested cancellation' },
      });
      setConversation(response.data);
      setPhase('polling');
      pollCountRef.current = 0;
      pollConversation(response.data._id);
    } catch (err) {
      setSubmitError(err.message);
      setPhase('error');
    }
  };

  // Retries whichever step actually failed: resume polling an existing
  // conversation, or re-submit the cancellation if it never got created.
  const handleRetry = () => {
    setSubmitError(null);
    if (lastConversationIdRef.current) {
      setPhase('polling');
      pollCountRef.current = 0;
      pollConversation(lastConversationIdRef.current);
    } else {
      handleConfirmCancel();
    }
  };

  const handleSelectRefundMethod = async (method) => {
    if (!conversation?._id) return;
    setSelectingMethod(true);
    try {
      const response = await supportConversationsApi.selectRefundMethod(conversation._id, method);
      setConversation(response.data);
      showToast(`Refund will be credited to ${method === 'wallet' ? 'your wallet' : 'your original payment method'}`);
    } catch (err) {
      showToast(err.message, { tone: 'error' });
    } finally {
      setSelectingMethod(false);
    }
  };

  return (
    <>
      <Navbar />
      <section className="py-24 min-h-[70vh] bg-gradient-to-b from-white to-canvas">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center shrink-0">
              <MessageCircle size={18} className="text-white" aria-hidden="true" />
            </div>
            <h1 className="serif text-2xl text-emerald-950">Cancel Order</h1>
          </div>

          {orderStatus === 'loading' && <LoadingState label="Checking order status..." />}
          {orderStatus === 'error' && <ErrorState message={orderError} onRetry={() => navigate(0)} />}

          {orderStatus === 'success' && order && (
            <div className="space-y-4">
              {/* Already cancelled */}
              {cancellationInfo && phase !== 'done-approved' && (
                <BotBubble>
                  <p className="font-semibold text-emerald-950 mb-1">This order has already been cancelled.</p>
                  {cancellationInfo.reason && (
                    <p className="text-ink-secondary">Reason: {cancellationInfo.reason}</p>
                  )}
                  {cancellationInfo.refundStatus && (
                    <p className="text-ink-secondary">
                      Refund status: {cancellationInfo.refundStatus === 'refunded' ? 'Refunded' : 'In progress'}
                    </p>
                  )}
                </BotBubble>
              )}

              {/* Pending request already exists */}
              {!cancellationInfo && pendingRequestId && phase === 'review' && (
                <BotBubble>
                  <p className="font-semibold text-emerald-950">
                    A cancellation request for this order is already being reviewed.
                  </p>
                  <p className="text-ink-secondary mt-1">We'll update you once our team has reviewed it.</p>
                </BotBubble>
              )}

              {needsConversationRecovery && recoveryError && (
                <BotBubble>
                  <p className="text-red-700 mb-3">{recoveryError}</p>
                  <Button onClick={() => setRecoveryAttempt((attempt) => attempt + 1)} className="w-full">
                    Check Status Again
                  </Button>
                </BotBubble>
              )}

              {/* Not eligible */}
              {!cancellationInfo && !pendingRequestId && !canCancel && phase === 'review' && (
                <BotBubble>
                  <div className="flex items-start gap-2">
                    <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="font-semibold text-emerald-950">
                      This order has already been packed and can no longer be cancelled.
                    </p>
                  </div>
                </BotBubble>
              )}

              {/* Eligible: review + confirm */}
              {!cancellationInfo && !pendingRequestId && canCancel && (phase === 'review' || phase === 'confirm') && (
                <>
                  <BotBubble>
                    <div className="flex items-start gap-2 mb-2">
                      <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="font-semibold text-emerald-950">This order is eligible for cancellation.</p>
                    </div>
                    <div className="text-ink-secondary space-y-0.5">
                      <p>Order: {order.invoiceNumber || `#${order._id}`}</p>
                      <p>Total: {formatCurrency(order.grandTotal)}</p>
                      <p>Items: {(order.items || []).length}</p>
                    </div>
                  </BotBubble>

                  {phase === 'review' && (
                    <div className="bg-white rounded-2xl border border-emerald-100 shadow-card p-4">
                      <label htmlFor="cancel-reason" className="block text-sm font-medium text-emerald-950 mb-2">
                        Reason for cancellation (optional)
                      </label>
                      <textarea
                        id="cancel-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder="Let us know why you're cancelling…"
                        className="w-full resize-none rounded-xl border border-emerald-200 px-3 py-2 text-sm text-emerald-950 placeholder:text-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      />
                      <Button onClick={() => setPhase('confirm')} className="mt-3 w-full">
                        <Ban size={16} aria-hidden="true" />
                        Continue to Cancel
                      </Button>
                    </div>
                  )}

                  {phase === 'confirm' && (
                    <BotBubble>
                      <p className="font-semibold text-emerald-950 mb-3">
                        Are you sure you want to cancel this order?
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleConfirmCancel}
                          className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
                        >
                          Yes, Cancel Order
                        </button>
                        <Button variant="secondary" onClick={() => setPhase('review')} className="flex-1 py-2.5 text-sm">
                          No, Go Back
                        </Button>
                      </div>
                    </BotBubble>
                  )}
                </>
              )}

              {phase === 'submitting' && (
                <BotBubble>
                  <div className="flex items-center gap-2">
                    <Loader2 className="animate-spin text-emerald-600" size={16} aria-hidden="true" />
                    <span>Submitting your cancellation request…</span>
                  </div>
                </BotBubble>
              )}

              {phase === 'polling' && (
                <BotBubble>
                  <div className="flex items-center gap-2">
                    <Loader2 className="animate-spin text-emerald-600" size={16} aria-hidden="true" />
                    <span>Your cancellation request has been submitted and is being reviewed…</span>
                  </div>
                </BotBubble>
              )}

              {phase === 'polling-timeout' && (
                <BotBubble>
                  <p className="font-semibold text-emerald-950">
                    Your cancellation request is still being reviewed.
                  </p>
                  <p className="text-ink-secondary mt-1">
                    We'll notify you once it's processed — you can also check back on this order's details page.
                  </p>
                  <Button onClick={() => setRecoveryAttempt((attempt) => attempt + 1)} className="mt-3 w-full">
                    Check Status Again
                  </Button>
                </BotBubble>
              )}

              {phase === 'done-approved' && (
                <BotBubble>
                  <div className="flex items-start gap-2 mb-2">
                    <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="font-semibold text-emerald-950">Your order has been cancelled.</p>
                  </div>
                  <div className="text-ink-secondary space-y-0.5">
                    <p>Order ID: {order._id}</p>
                    {conversation?.refund?.status && conversation.refund.status !== 'not_required' && (
                      <p>Refund status: {conversation.refund.status}</p>
                    )}
                    {conversation?.refund?.selectedAt && (
                      <p>Refund initiated: {new Date(conversation.refund.selectedAt).toLocaleString('en-IN')}</p>
                    )}
                  </div>

                  {conversation?.refund?.selectionRequired && (
                    <div className="mt-4 pt-4 border-t border-emerald-50">
                      <p className="font-semibold text-emerald-950 mb-1">Choose how you'd like your refund</p>
                      <p className="text-ink-secondary mb-3">
                        Select where we should send ₹{conversation.refund.amount ?? ''}.
                      </p>
                      <div className="flex gap-3">
                        {(conversation.refund.availableMethods || []).map((method) => (
                          <Button
                            key={method}
                            variant="secondary"
                            disabled={selectingMethod}
                            onClick={() => handleSelectRefundMethod(method)}
                            className="flex-1 py-2.5 text-sm"
                          >
                            {method === 'wallet' ? 'App Wallet' : 'Original Payment Method'}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </BotBubble>
              )}

              {phase === 'done-resolved' && (
                <BotBubble>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-semibold text-emerald-950">Your cancellation request has been resolved.</p>
                      <p className="text-ink-secondary mt-1">
                        Our team has closed this request. Check your order details for the latest status, or contact
                        support if you still need help.
                      </p>
                    </div>
                  </div>
                </BotBubble>
              )}

              {phase === 'done-rejected' && (
                <BotBubble>
                  <div className="flex items-start gap-2">
                    <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-semibold text-emerald-950">Your cancellation request was not approved.</p>
                      {conversation?.decision?.note && (
                        <p className="text-ink-secondary mt-1">{conversation.decision.note}</p>
                      )}
                    </div>
                  </div>
                </BotBubble>
              )}

              {phase === 'error' && (
                <BotBubble>
                  <div className="flex items-start gap-2 mb-3">
                    <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-emerald-950">{submitError || 'Something went wrong. Please try again.'}</p>
                  </div>
                  <Button onClick={handleRetry} className="w-full">
                    Try Again
                  </Button>
                </BotBubble>
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CancelOrderChatPage;
